import { OrganizationInvoiceStatus } from '@breakoutlearning/firebase-repository/types'
import {
  BreakoutRadioButton,
  BreakoutRadioGroup,
} from 'components/design-system/BreakoutRadioButton'
import { FormError } from 'components/design-system/form/FormError'
import {
  FormLabel,
  HasNoAssociatedFormField,
} from 'components/design-system/form/FormLabel'
import { useTranslationTyped } from 'i18n/i18n'
import type { UseFormReturn } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import type { FormValues } from './schema'

export function BillingPartySection({
  invoiceStatus,
  form,
}: {
  invoiceStatus: OrganizationInvoiceStatus
  form: UseFormReturn<FormValues>
}) {
  const { tt } = useTranslationTyped()
  return (
    <>
      {invoiceStatus === OrganizationInvoiceStatus.adminApproved && (
        <BillingPartySelector form={form} />
      )}

      {invoiceStatus === OrganizationInvoiceStatus.noInvoices && (
        <div>
          <div className="mb-2 flex flex-row items-center justify-start gap-1">
            <FormLabel
              labelClass="!pr-0 !w-auto"
              required
              label={tt.lti.configuration.who_pays_label()}
              for={HasNoAssociatedFormField}
            />
          </div>
          <div className="pl-4 text-label-large">
            {tt.lti.configuration.participant_pay_subtitle_full()}
          </div>
        </div>
      )}

      {invoiceStatus === OrganizationInvoiceStatus.alwaysInvoice && (
        <div>
          <div className="mb-2 flex flex-row items-center justify-start gap-1">
            <FormLabel
              labelClass="!pr-0 !w-auto"
              required
              label={tt.lti.configuration.who_pays_label()}
              for={HasNoAssociatedFormField}
            />
          </div>
          <div className="pl-4 text-label-large">
            {tt.lti.configuration.institution_pay_subtitle_full()}
          </div>
        </div>
      )}
    </>
  )
}

function BillingPartySelector({ form }: { form: UseFormReturn<FormValues> }) {
  const { tt } = useTranslationTyped()
  const { control } = form
  return (
    <div>
      <div className="mb-2 flex flex-row items-center justify-start gap-1">
        <FormLabel
          labelClass="!pr-0 !w-auto"
          required
          label={tt.lti.configuration.who_pays_label()}
          for={HasNoAssociatedFormField}
        />
      </div>
      <Controller
        control={control}
        name="billingParty"
        render={({ field, fieldState }) => {
          return (
            <div>
              <BreakoutRadioGroup
                aria-label={tt.lti.configuration.who_pays_label()}
                value={field.value ?? ''}
                onValueChange={field.onChange}
                className="flex flex-row gap-2"
              >
                <BreakoutRadioButton
                  label={
                    <div className="flex flex-1 flex-col gap-1">
                      <div className="whitespace-nowrap text-title-medium text-on-surface">
                        {tt.lti.configuration.participant_pay_title()}
                      </div>
                      <div className="text-body-medium text-on-surface-var">
                        {tt.lti.configuration.participant_pay_subtitle()}
                      </div>
                    </div>
                  }
                  kind="tertiary"
                  variant="compact"
                  value="student"
                />
                <BreakoutRadioButton
                  label={
                    <div className="flex flex-1 flex-col gap-1">
                      <div className="whitespace-nowrap text-title-medium text-on-surface">
                        {tt.lti.configuration.institution_pay_title()}
                      </div>
                      <div className="text-body-medium text-on-surface-var">
                        {tt.lti.configuration.institution_pay_subtitle()}
                      </div>
                    </div>
                  }
                  kind="tertiary"
                  variant="compact"
                  value="invoicing"
                />
              </BreakoutRadioGroup>
              <FormError error={fieldState.error} />
            </div>
          )
        }}
      />
    </div>
  )
}
